import React, { useMemo } from "react";
import { Badge, Flex, Spinner, Text } from "@radix-ui/themes";
import { Table } from "@radix-ui/themes";
import { useNavigate } from "react-router-dom";

import { Version } from "@/v2/entities/release/Release.model";
import { useReleaseStats } from "../simulator/context/useReleaseStats";

import styles from "./styles.module.scss";

interface VersionItemProps {
  version: Version;
  index: number;
  count: number;
  instructionsCount: number;
  topicsCount: number;
  topicsList: string[];
  selectedTopicIds: number[];
}

export function VersionItem({
  version,
  index,
  count,
  instructionsCount,
  topicsCount,
  topicsList,
  selectedTopicIds,
}: VersionItemProps) {
  const navigate = useNavigate();
  const { conversations, isLoading } = useReleaseStats(version.id.toString());

  const testedInteractionsCount = useMemo(
    () =>
      conversations?.filter(
        (conversation) =>
          selectedTopicIds.length === 0 ||
          selectedTopicIds.includes(conversation.topicId || 0)
      ).length || 0,
    [conversations, selectedTopicIds]
  );

  const goodFeedbackCount = useMemo(
    () =>
      conversations?.filter(
        (conversation) =>
          conversation.feedback === "POSITIVE" &&
          (selectedTopicIds.length === 0 ||
            selectedTopicIds.includes(conversation.topicId || 0))
      ).length || 0,
    [conversations, selectedTopicIds]
  );

  const badFeedbackCount = useMemo(
    () =>
      conversations?.filter(
        (conversation) =>
          conversation.feedback === "NEGATIVE" &&
          (selectedTopicIds.length === 0 ||
            selectedTopicIds.includes(conversation.topicId || 0))
      ).length || 0,
    [conversations, selectedTopicIds]
  );

  const isDraft = version.status === "draft" && !version.publishedAt;

  const handleVersionClick = () => {
    navigate(`/automation/${version.simulatorId}/versions/${version.id}`);
  };

  return (
    <Table.Row
      className="cursor-pointer hover:bg-gray-100"
      onClick={handleVersionClick}
    >
      <Table.Cell>
        <Flex align={"center"} gap={"2"}>
          Version {count - index}
          {version.status === "published" && (
            <Badge size="2" color="green">
              Live Version
            </Badge>
          )}
          {isDraft && (
            <Badge size="2" color="gray">
              Currently in training
            </Badge>
          )}
        </Flex>
      </Table.Cell>
      <Table.Cell>
        {topicsCount > 0 ? (
          <>
            <Text size="2">{topicsList.length}</Text>
            <Flex gap="1" wrap="wrap" className={styles.topicsList}>
              {topicsList.map((topic, i) => (
                <Badge key={i} size="1" variant="soft" color="gray">
                  {typeof topic === "string" ? topic : String(topic)}
                </Badge>
              ))}
            </Flex>
          </>
        ) : (
          <Text size="2" color="gray">
            No topics released
          </Text>
        )}
      </Table.Cell>
      <Table.Cell>
        <Text size="2">{instructionsCount}</Text>
      </Table.Cell>
      <Table.Cell>
        {isLoading ? <Spinner size="1" /> : testedInteractionsCount}
      </Table.Cell>
      <Table.Cell>
        <Badge size="2" color="green">
          {isLoading ? <Spinner size="1" /> : goodFeedbackCount}
        </Badge>
      </Table.Cell>
      <Table.Cell>
        <Badge size="2" color="red">
          {isLoading ? <Spinner size="1" /> : badFeedbackCount}
        </Badge>
      </Table.Cell>
      <Table.Cell onClick={(e) => e.stopPropagation()}>
        {version.publishedAt
          ? new Date(version.publishedAt).toLocaleDateString()
          : "-"}
      </Table.Cell>
    </Table.Row>
  );
}
